/**
 * Technicians — assign jobs, inspection / completion videos, send or record quotes
 * (email, WhatsApp, phone, in person), and authorise work with a notes box.
 */
import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { useUserRole } from '../hooks/useUserRole';
import { formatCurrency } from '../utils/helpers';
import { sendQuoteToCustomer } from '../services/firebaseFunctions';
import { sendWhatsApp } from '../services/whatsappService';
import {
  buildQuoteWhatsAppPayload,
  buildQuoteTemplatePayload,
  normalizePhoneForWhatsApp,
  alertWhatsAppSent,
} from '../utils/whatsappCustomerUpdates';
import { whatsappConfig } from '../config/app';
import { currentCompanyName } from '../firebase/config';
import { vehicleMediaService } from '../services/storageService';
import { isStageEnabled } from '../utils/enabledStages';
import { APPROVAL_STATUSES, QUOTE_WORKFLOW_STAGES, getQuoteStageDisplay, getWorkshopWorkflowCopy } from '../utils/jobTraffic';

export { APPROVAL_STATUSES };

const ActionButton = ({ children, onClick, disabled, variant = 'primary' }) => {
  const isDanger = variant === 'danger';
  const isSecondary = variant === 'secondary';
  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!disabled && onClick) onClick(e);
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={`ui-btn${isSecondary ? ' ui-btn--secondary' : ''}${isDanger ? ' ui-btn--danger' : ''}`}
    >
      {children}
    </button>
  );
};

const GARAGE_STATUSES = ['MOT', 'Bodyshop', 'Workshop', 'Tyres'];
const IN_PROGRESS_STATUSES = ['In Progress', 'Awaiting Parts'];

const getGarageCategory = (veh) => {
  const status = veh.status || '';
  if (GARAGE_STATUSES.includes(status)) return status;
  if (IN_PROGRESS_STATUSES.includes(status)) return veh.previousStatus || 'Workshop';
  return null;
};

const getCustomerEmail = (job) => {
  if (job.email && job.email.includes('@')) return job.email;
  const match = (job.contact || '').match(/[\w.-]+@[\w.-]+\.\w+/);
  return match ? match[0] : null;
};

/** Contact field is phone digits — used for optional WhatsApp */
const canSendCustomerWhatsApp = (job) => !!normalizePhoneForWhatsApp(job.contact || '');

/** Same matching rules as elsewhere: exact name or substring match for display names */
const matchesAssignedTech = (job, currentUsername) => {
  if (!job?.technician || !currentUsername) return false;
  const t = (job.technician || '').toLowerCase().trim();
  const u = (currentUsername || '').toLowerCase().trim();
  return t === u || t.includes(u) || u.includes(t);
};

const getJobMonthKey = (job) => {
  const raw = job?.dropOffDate || job?.receivedDate || '';
  const key = String(raw).slice(0, 7);
  return /^\d{4}-\d{2}$/.test(key) ? key : '';
};

const currentMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const TechnicianDispatch = ({ onOpenVehicleDetail }) => {
  const { vehicles, garageInfo, updateVehicle } = useApp();
  const { role: currentUserRole } = useUserRole();
  const [techSearch, setTechSearch] = useState('');
  const [monthFilter, setMonthFilter] = useState(currentMonthKey);
  const [sendingQuoteReg, setSendingQuoteReg] = useState(null);
  const [updatingStatusReg, setUpdatingStatusReg] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(null);
  const [expandedJobReg, setExpandedJobReg] = useState(null);
  const [jobApprovalNotesByReg, setJobApprovalNotesByReg] = useState({});

  const isManager =
    currentUserRole === 'owner' || currentUserRole === 'admin' || currentUserRole === 'manager';
  const canSendQuote =
    isManager || currentUserRole === 'sales' || currentUserRole === 'technician';
  const isTechnicianRole = currentUserRole === 'technician';
  const currentUsername =
    (typeof window !== 'undefined' &&
      (sessionStorage.getItem('username') || sessionStorage.getItem('userDisplayName') || '')) ||
    '';

  const technicians = useMemo(() => {
    const list = garageInfo?.technicians || [];
    const assigned = new Set(
      (vehicles || [])
        .filter((v) => !v.deletedAt && v.technician)
        .map((v) => v.technician)
    );
    return [...new Set([...list, ...assigned])].sort((a, b) => (a || '').localeCompare(b || ''));
  }, [vehicles, garageInfo?.technicians]);

  const garageJobs = useMemo(() => {
    return (vehicles || [])
      .map((v, originalIndex) => ({
        ...v,
        category: getGarageCategory(v),
        index: originalIndex
      }))
      .filter((v) => {
        if (v.deletedAt) return false;
        if (v.category === null) return false;
        return isStageEnabled(garageInfo, v.category);
      })
      .filter((j) => {
        if (isTechnicianRole && currentUsername) {
          if (!matchesAssignedTech(j, currentUsername)) return false;
        }
        const search = techSearch.trim().toLowerCase();
        if (search) {
          const techName = (j.technician || 'Unassigned').toLowerCase();
          if (!techName.includes(search)) return false;
        }
        if (monthFilter && getJobMonthKey(j) !== monthFilter) return false;
        return true;
      })
      .sort((a, b) => (a.reg || '').localeCompare(b.reg || '', undefined, { sensitivity: 'base' }));
  }, [vehicles, techSearch, monthFilter, isTechnicianRole, currentUsername, garageInfo]);

  const jobsByTech = useMemo(() => {
    const map = new Map();
    garageJobs.forEach((j) => {
      const tech = j.technician || 'Unassigned';
      if (!map.has(tech)) map.set(tech, []);
      map.get(tech).push(j);
    });
    const search = techSearch.trim().toLowerCase();
    if (search) {
      technicians.forEach((t) => {
        if ((t || '').toLowerCase().includes(search) && !map.has(t)) map.set(t, []);
      });
      if ('unassigned'.includes(search) && !map.has('Unassigned')) map.set('Unassigned', []);
    }
    return [...map.entries()].sort((a, b) =>
      a[0].localeCompare(b[0], undefined, { sensitivity: 'base' })
    );
  }, [garageJobs, techSearch, technicians]);

  const pushHistory = (job, entry) => [
    ...(job.approvalHistory || []),
    { ...entry, at: new Date().toISOString() }
  ];

  const handleAssignTechnician = (job, technicianName) => {
    if (job.index == null || job.index < 0) return;
    updateVehicle(job.index, {
      technician: technicianName || '',
      approvalHistory: pushHistory(job, {
        status: 'technician_assigned',
        note: technicianName ? `Assigned to ${technicianName}` : 'Unassigned'
      })
    });
  };

  const handleSendQuote = async (job, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (job.index === null || job.index === undefined || job.index < 0) return;
    const email = getCustomerEmail(job);
    const toUse = email || window.prompt('Enter customer email address:');
    if (!toUse || !toUse.includes('@')) {
      if (!email) window.alert('Valid email required.');
      return;
    }
    setSendingQuoteReg(job.reg);
    try {
      await sendQuoteToCustomer({
        to: toUse,
        companyName: garageInfo?.companyName || 'Garage',
        vehicleReg: job.reg,
        vehicleName: job.vehicle,
        customerName: job.customerName || 'Customer',
        estimateAmount: job.technicianEstimate?.amount,
        estimateDescription: job.technicianEstimate?.description,
        summary: job.summary,
        inspectionVideoUrl: job.inspectionVideoUrl || undefined,
        finalWorkVideoUrl: job.finalWorkVideoUrl || undefined
      });
      updateVehicle(job.index, {
        approvalStatus: 'quote_sent_to_customer',
        approvalHistory: pushHistory(job, {
          status: 'quote_sent_email',
          note: `Quote emailed to ${toUse}`
        })
      });
      window.alert(`Quote emailed to ${toUse}.`);
    } catch (err) {
      window.alert(err.message || 'Failed to send quote email.');
    } finally {
      setSendingQuoteReg(null);
    }
  };

  const handleSendQuoteWhatsApp = async (job, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (job.index == null || job.index < 0 || !job.technicianEstimate) {
      window.alert('Add an estimate before sending a quote.');
      return;
    }
    const phoneRaw = (job.contact || '').trim();
    if (!phoneRaw || !normalizePhoneForWhatsApp(phoneRaw)) {
      window.alert('Add a valid mobile number in Customer contact to send the quote on WhatsApp.');
      return;
    }
    setSendingQuoteReg(job.reg);
    try {
      const est = job.technicianEstimate || {};
      const tplName = (whatsappConfig && whatsappConfig.quoteTemplateName) || 'quote';
      if (tplName) {
        const tpl = buildQuoteTemplatePayload({
          vehicleReg: job.reg,
          vehicleName: job.vehicle,
          summary: job.summary,
          estimateAmount: est.amount,
          estimateDescription: est.description,
          companyName: garageInfo?.companyName || 'Garage',
        });
        await sendWhatsApp(phoneRaw, '', { template: tpl, companyId: currentCompanyName });
      } else {
        const { message, media } = buildQuoteWhatsAppPayload({
          companyName: garageInfo?.companyName || 'Garage',
          vehicleReg: job.reg,
          vehicleName: job.vehicle,
          customerName: job.customerName || 'Customer',
          estimateAmount: est.amount,
          estimateDescription: est.description,
          summary: job.summary,
          inspectionVideoUrl: job.inspectionVideoUrl || undefined,
          finalWorkVideoUrl: job.finalWorkVideoUrl || undefined
        });
        await sendWhatsApp(phoneRaw, message, media.length ? { media, companyId: currentCompanyName } : { companyId: currentCompanyName });
      }
      updateVehicle(job.index, {
        approvalStatus: 'quote_sent_to_customer',
        approvalHistory: pushHistory(job, { status: 'quote_sent_whatsapp', note: 'Quote sent on WhatsApp' })
      });
      alertWhatsAppSent('Quote sent on WhatsApp.', phoneRaw);
    } catch (err) {
      window.alert(err.message || 'WhatsApp send failed.');
    } finally {
      setSendingQuoteReg(null);
    }
  };

  const handleRecordQuoteGiven = (job, channel, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (job.index == null || job.index < 0 || !job.technicianEstimate) {
      window.alert('Add an estimate before recording a quote.');
      return;
    }
    const events = {
      phone: { status: 'quote_given_phone', note: 'Quote given by phone' },
      in_person: { status: 'quote_given_in_person', note: 'Quote given in person' }
    };
    const ev = events[channel];
    if (!ev) return;
    updateVehicle(job.index, {
      approvalStatus: 'quote_sent_to_customer',
      approvalHistory: pushHistory(job, ev)
    });
  };

  /** Inspection-only email (e.g. customer approves work after seeing video) */
  const handleSendInspectionToCustomer = async (job, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (!job.inspectionVideoUrl) {
      window.alert('Add an inspection video first.');
      return;
    }
    if (job.index === null || job.index === undefined || job.index < 0) return;
    const email = getCustomerEmail(job);
    const toUse = email || window.prompt('Enter customer email address:');
    if (!toUse || !toUse.includes('@')) {
      if (!email) window.alert('Valid email required.');
      return;
    }
    setSendingQuoteReg(job.reg);
    try {
      await sendQuoteToCustomer({
        to: toUse,
        companyName: garageInfo?.companyName || 'Garage',
        vehicleReg: job.reg,
        vehicleName: job.vehicle,
        customerName: job.customerName || 'Customer',
        estimateAmount: job.technicianEstimate?.amount ?? null,
        estimateDescription: job.technicianEstimate?.description,
        summary:
          (job.summary || '').trim() ||
          'Inspection video for your review — please approve or discuss before we proceed with work.',
        inspectionVideoUrl: job.inspectionVideoUrl,
        finalWorkVideoUrl: job.finalWorkVideoUrl || undefined
      });
      if (
        canSendCustomerWhatsApp(job) &&
        window.confirm('Also send the inspection details on WhatsApp (including video links)?')
      ) {
        try {
          const { message, media } = buildQuoteWhatsAppPayload({
            companyName: garageInfo?.companyName || 'Garage',
            vehicleReg: job.reg,
            vehicleName: job.vehicle,
            customerName: job.customerName || 'Customer',
            estimateAmount: job.technicianEstimate?.amount ?? null,
            estimateDescription: job.technicianEstimate?.description,
            summary:
              (job.summary || '').trim() ||
              'Inspection video for your review — please approve or discuss before we proceed with work.',
            inspectionVideoUrl: job.inspectionVideoUrl,
            finalWorkVideoUrl: job.finalWorkVideoUrl || undefined
          });
          await sendWhatsApp(job.contact, message, media.length ? { media } : {});
          alertWhatsAppSent('Inspection details sent on WhatsApp.', job.contact);
        } catch (waErr) {
          window.alert(waErr.message || 'WhatsApp send failed (email was sent).');
        }
      }
      updateVehicle(job.index, {
        approvalHistory: pushHistory(job, {
          status: 'inspection_emailed',
          note: 'Inspection video emailed to customer'
        })
      });
    } catch (err) {
      window.alert(err.message || 'Failed to send email.');
    } finally {
      setSendingQuoteReg(null);
    }
  };

  const handleVideoUpload = async (job, file, kind) => {
    if (!file?.size || job.index == null || job.index < 0) return;
    const key = `${job.reg}-${kind}`;
    setUploadingVideo(key);
    try {
      const url = await vehicleMediaService.uploadVideo(file, job.reg, kind);
      if (kind === 'inspection') {
        updateVehicle(job.index, {
          inspectionVideoUrl: url,
          inspectionVideoUploadedAt: new Date().toISOString(),
          approvalHistory: pushHistory(job, { status: 'inspection_video_uploaded', note: 'Inspection video saved' })
        });
      } else {
        updateVehicle(job.index, {
          finalWorkVideoUrl: url,
          finalWorkVideoUploadedAt: new Date().toISOString(),
          approvalHistory: pushHistory(job, { status: 'final_video_uploaded', note: 'Completion video saved' })
        });
      }
    } catch (err) {
      window.alert(err.message || 'Upload failed.');
    } finally {
      setUploadingVideo(null);
    }
  };

  const handleSubmitPreWork = (job, e) => {
    e?.stopPropagation?.();
    if (job.index == null || job.index < 0) return;
    updateVehicle(job.index, {
      preWorkSubmissionStatus: 'pending_manager',
      preWorkSubmittedAt: new Date().toISOString(),
      approvalHistory: pushHistory(job, {
        status: 'pre_work_submitted',
        note: 'Technician submitted inspection / notes for manager approval before work'
      })
    });
  };

  const handleManagerPreWorkApprove = (job, e) => {
    e?.stopPropagation?.();
    if (job.index == null || job.index < 0) return;
    updateVehicle(job.index, {
      preWorkSubmissionStatus: 'approved',
      preWorkApprovedAt: new Date().toISOString(),
      approvalHistory: pushHistory(job, { status: 'pre_work_approved', note: 'Manager approved proceeding' })
    });
  };

  const handleManagerPreWorkReject = (job, e) => {
    e?.stopPropagation?.();
    if (job.index == null || job.index < 0) return;
    const note = window.prompt('Reason (optional):') || 'Rejected by manager';
    updateVehicle(job.index, {
      preWorkSubmissionStatus: 'rejected',
      preWorkRejectionNote: note,
      approvalHistory: pushHistory(job, { status: 'pre_work_rejected', note })
    });
  };

  const handleStatusChange = (job, newStatus) => {
    if (job.index === null || job.index === undefined || job.index < 0) return;
    const updates = {
      approvalStatus: newStatus,
      approvalHistory: pushHistory(job, { status: newStatus })
    };
    if (newStatus === 'job_authorised') {
      const note = (jobApprovalNotesByReg[job.reg] || job.jobApprovalNote || '').trim();
      if (!note) {
        window.alert('Add a note, e.g. “Job approved over phone for £295”.');
        return;
      }
      updates.jobAuthorisedNotificationRead = false;
      updates.jobApprovalNote = note;
      updates.approvalHistory = pushHistory(job, { status: newStatus, note });
    }
    setUpdatingStatusReg(job.reg);
    try {
      updateVehicle(job.index, updates);
    } finally {
      setUpdatingStatusReg(null);
    }
  };

  const handleApprove = (job, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (job.index === null || job.index === undefined || job.index < 0) return;
    const note = (jobApprovalNotesByReg[job.reg] || job.jobApprovalNote || '').trim();
    if (!note) {
      window.alert('Add a note, e.g. “Job approved over phone for £295”.');
      return;
    }
    updateVehicle(job.index, {
      approvalStatus: 'job_authorised',
      jobAuthorisedNotificationRead: false,
      jobApprovalNote: note,
      approvalHistory: pushHistory(job, { status: 'job_authorised', note })
    });
  };

  const handleMarkNotificationRead = (job, e) => {
    e?.stopPropagation?.();
    if (job.index === null || job.index === undefined || job.index < 0) return;
    updateVehicle(job.index, { jobAuthorisedNotificationRead: true });
  };

  const handleReject = (job, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (job.index === null || job.index === undefined || job.index < 0) return;
    const note = window.prompt('Rejection reason (optional):') || 'Rejected by manager';
    updateVehicle(job.index, {
      approvalStatus: 'in_review',
      approvalHistory: pushHistory(job, { status: 'rejected', note })
    });
  };

  const canTechAct = (job) =>
    isTechnicianRole && currentUsername && matchesAssignedTech(job, currentUsername);

  return (
    <div className="vehicle-inner">
      <div
        className="vehicle-header"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '24px'
        }}
      >
        <h2 style={{ margin: 0 }}>Technicians</h2>
        <label className="search-field" style={{ flex: '1 1 200px', minWidth: 0, maxWidth: '280px' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M10.5 3.75a6.75 6.75 0 1 1 0 13.5 6.75 6.75 0 0 1 0-13.5Zm0 1.5a5.25 5.25 0 1 0 0 10.5 5.25 5.25 0 0 0 0-10.5Zm7.97 11.28 3.15 3.15-1.06 1.06-3.15-3.15 1.06-1.06Z"
              fill="currentColor"
            />
          </svg>
          <input
            type="search"
            value={techSearch}
            onChange={(e) => setTechSearch(e.target.value)}
            placeholder="Search by name"
            aria-label="Search by technician name"
          />
        </label>
        <label className="tech-jobs-month">
          <span>Month</span>
          <input
            type="month"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            aria-label="Filter by month"
          />
        </label>
      </div>

      <div className="tech-jobs-board">
        {jobsByTech.map(([tech, jobs]) => (
          <section className="tech-jobs-col" key={tech}>
            <div className="tech-jobs-col__head">
              <div className="tech-jobs-col__name">{tech}</div>
              <div className="tech-jobs-col__total">Total {jobs.length}</div>
            </div>
            <div className="tech-jobs-col__regs">
        {jobs.map((job) => {
                const statusConfig = getQuoteStageDisplay(job);
                const wf = getWorkshopWorkflowCopy(job);
                const isAssignedToMe = matchesAssignedTech(job, currentUsername);
                const hasUnreadNotification =
                  job.approvalStatus === 'job_authorised' && !job.jobAuthorisedNotificationRead && isAssignedToMe;
                const pre = job.preWorkSubmissionStatus;

                const isExpanded = expandedJobReg === job.reg;

                return (
                  <div
                    key={job.reg}
                    className={`tech-job-row${isExpanded ? ' is-open' : ''}`}
                    data-selected={isExpanded ? 'true' : 'false'}
                    title={`${job.technician || 'Unassigned'} — ${job.reg}`}
                    onClick={() => {
                      setExpandedJobReg((prev) => (prev === job.reg ? null : job.reg));
                      if (hasUnreadNotification) updateVehicle(job.index, { jobAuthorisedNotificationRead: true });
                    }}
                  >
                    <div className="tech-job-row__bar">
                      <span className="vehicle-card-reg">{job.reg || '—'}</span>
                    </div>
                    {isExpanded && (
                    <div className="tech-job-row__details" onClick={(e) => e.stopPropagation()}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ marginBottom: '10px' }}>
                      <ActionButton variant="secondary" onClick={() => onOpenVehicleDetail?.(job.index)}>
                        Open job card
                      </ActionButton>
                    </div>
                    {hasUnreadNotification && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          marginBottom: '8px',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                          color: '#fff',
                          fontSize: '13px',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px'
                        }}
                      >
                        <span>✓ Job authorised – ready to proceed</span>
                        <button
                          type="button"
                          onClick={(e) => handleMarkNotificationRead(job, e)}
                          style={{
                            padding: '4px 12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            borderRadius: '8px',
                            border: '1px solid rgba(255,255,255,0.8)',
                            background: 'rgba(255,255,255,0.2)',
                            color: '#fff',
                            cursor: 'pointer'
                          }}
                        >
                          Got it
                        </button>
                      </div>
                    )}

                    {isManager && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ marginBottom: '10px', fontSize: '13px' }}
                      >
                        <span style={{ fontWeight: 600, marginRight: '8px' }}>Assign to:</span>
                        <select
                          value={job.technician || ''}
                          onChange={(e) => handleAssignTechnician(job, e.target.value)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '8px',
                            border: '1px solid var(--border, #e5e7eb)',
                            maxWidth: '100%'
                          }}
                        >
                          <option value="">Unassigned</option>
                          {technicians.map((t) => (
                            <option key={t || 'x'} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '15px', fontWeight: 700, letterSpacing: '0.02em' }}>
                          {job.reg} <span style={{ fontWeight: 500, color: '#64748b' }}>·</span> {job.vehicle}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-dark, #334155)', marginTop: '4px', lineHeight: 1.3 }}>
                          {wf.headline}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', lineHeight: 1.4 }}>
                          {wf.detail}
                        </div>
                      </div>
                      <div style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                        {isManager && job.technicianEstimate ? (
                          <select
                            value={job.approvalStatus || 'in_review'}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleStatusChange(job, e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            disabled={updatingStatusReg === job.reg}
                            title="Quote / customer stage"
                            style={{
                              fontSize: '11px',
                              maxWidth: '160px',
                              padding: '6px 8px',
                              borderRadius: '8px',
                              border: '1px solid rgba(0,0,0,0.12)',
                              backgroundColor: '#fff',
                              color: '#0f172a',
                              cursor: updatingStatusReg === job.reg ? 'wait' : 'pointer',
                              fontWeight: 600
                            }}
                          >
                            {QUOTE_WORKFLOW_STAGES.map((k) => (
                              <option key={k} value={k}>
                                {APPROVAL_STATUSES[k].label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span
                            style={{
                              fontSize: '11px',
                              padding: '6px 10px',
                              borderRadius: '999px',
                              backgroundColor: `${statusConfig.color}18`,
                              color: statusConfig.color,
                              fontWeight: 700,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {statusConfig.label}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: '13px', color: '#475569', marginBottom: '10px', lineHeight: 1.45 }}>
                      {job.summary || job.category}
                    </div>

                    {pre === 'rejected' && job.preWorkRejectionNote && (
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#b91c1c',
                          marginBottom: '8px',
                          padding: '8px 10px',
                          borderRadius: '8px',
                          background: 'rgba(239, 68, 68, 0.08)'
                        }}
                      >
                        Manager note: {job.preWorkRejectionNote}
                      </div>
                    )}

                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        marginBottom: '10px',
                        fontSize: '11px',
                        fontWeight: 600
                      }}
                    >
                      <span
                        style={{
                          padding: '4px 10px',
                          borderRadius: '999px',
                          background: job.inspectionVideoUrl ? 'rgba(34, 197, 94, 0.12)' : 'rgba(148, 163, 184, 0.2)',
                          color: job.inspectionVideoUrl ? '#15803d' : '#64748b'
                        }}
                      >
                        Inspection video {job.inspectionVideoUrl ? '●' : '○'}
                      </span>
                      <span
                        style={{
                          padding: '4px 10px',
                          borderRadius: '999px',
                          background: job.finalWorkVideoUrl ? 'rgba(34, 197, 94, 0.12)' : 'rgba(148, 163, 184, 0.2)',
                          color: job.finalWorkVideoUrl ? '#15803d' : '#64748b'
                        }}
                      >
                        Completion video {job.finalWorkVideoUrl ? '●' : '○'}
                      </span>
                    </div>

                    {/* Pre-work: inspection video + submit / manager decision */}
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        marginBottom: '10px',
                        padding: '10px',
                        borderRadius: '10px',
                        background: 'rgba(248, 250, 252, 0.9)',
                        border: '1px solid rgba(148, 163, 184, 0.25)'
                      }}
                    >
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Before work · inspection</div>
                      {job.inspectionVideoUrl && (
                        <video
                          src={job.inspectionVideoUrl}
                          controls
                          style={{
                            width: '100%',
                            maxHeight: '160px',
                            borderRadius: '8px',
                            marginBottom: '8px',
                            background: '#000'
                          }}
                        />
                      )}
                      {canTechAct(job) && (
                        <label style={{ display: 'block', fontSize: '12px', marginBottom: '8px', cursor: 'pointer' }}>
                          <span style={{ fontWeight: 600 }}>
                            {uploadingVideo === `${job.reg}-inspection` ? 'Uploading…' : 'Record or upload inspection video'}
                          </span>
                          <input
                            type="file"
                            accept="video/*"
                            capture="environment"
                            style={{ display: 'block', marginTop: '6px' }}
                            disabled={!!uploadingVideo}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              e.target.value = '';
                              if (f) handleVideoUpload(job, f, 'inspection');
                            }}
                          />
                        </label>
                      )}
                      {isManager && job.inspectionVideoUrl && (
                        <ActionButton
                          onClick={(e) => handleSendInspectionToCustomer(job, e)}
                          disabled={!!sendingQuoteReg}
                        >
                          {sendingQuoteReg === job.reg ? 'Sending…' : 'Email inspection to customer'}
                        </ActionButton>
                      )}
                      {canTechAct(job) && (pre === 'rejected' || !pre) && (
                        <div style={{ marginTop: '8px' }}>
                          <ActionButton onClick={(e) => handleSubmitPreWork(job, e)}>
                            Submit to manager for approval before work
                          </ActionButton>
                        </div>
                      )}
                      {canTechAct(job) && pre === 'pending_manager' && (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#92400e', fontWeight: 600 }}>
                          Submitted — waiting for manager approval
                        </div>
                      )}
                      {isManager && pre === 'pending_manager' && (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                          <ActionButton onClick={(e) => handleManagerPreWorkApprove(job, e)}>Approve pre-work</ActionButton>
                          <ActionButton variant="danger" onClick={(e) => handleManagerPreWorkReject(job, e)}>
                            Send back
                          </ActionButton>
                        </div>
                      )}
                    </div>

                    {/* Completion video */}
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        marginBottom: '10px',
                        padding: '10px',
                        borderRadius: '10px',
                        background: 'rgba(248, 250, 252, 0.9)',
                        border: '1px solid rgba(148, 163, 184, 0.25)'
                      }}
                    >
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>After work · completion</div>
                      {job.finalWorkVideoUrl && (
                        <video
                          src={job.finalWorkVideoUrl}
                          controls
                          style={{
                            width: '100%',
                            maxHeight: '160px',
                            borderRadius: '8px',
                            marginBottom: '8px',
                            background: '#000'
                          }}
                        />
                      )}
                      {canTechAct(job) && (
                        <label style={{ display: 'block', fontSize: '12px', cursor: 'pointer' }}>
                          <span style={{ fontWeight: 600 }}>
                            {uploadingVideo === `${job.reg}-final` ? 'Uploading…' : 'Record or upload completion video'}
                          </span>
                          <input
                            type="file"
                            accept="video/*"
                            capture="environment"
                            style={{ display: 'block', marginTop: '6px' }}
                            disabled={!!uploadingVideo}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              e.target.value = '';
                              if (f) handleVideoUpload(job, f, 'final');
                            }}
                          />
                        </label>
                      )}
                    </div>

                    {job.technicianEstimate && (
                      <div style={{ fontSize: '13px', marginBottom: '8px', fontWeight: 600, color: '#0f172a' }}>
                        Estimate {formatCurrency(job.technicianEstimate.amount || 0)}
                      </div>
                    )}
                    {job.approvalStatus === 'job_authorised' && job.jobApprovalNote && (
                      <div style={{ fontSize: '12px', color: '#059669', marginBottom: '8px', lineHeight: 1.4 }}>
                        {job.jobApprovalNote}
                      </div>
                    )}

                    <div
                      style={{
                        marginTop: '4px',
                        paddingTop: '10px',
                        borderTop: '1px dashed rgba(148, 163, 184, 0.45)'
                      }}
                    >
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Quote stage</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#334155', marginTop: '2px' }}>{statusConfig.label}</div>
                    </div>

                    {canSendQuote &&
                      job.technicianEstimate &&
                      (job.approvalStatus || 'in_review') !== 'job_authorised' && (
                      <div
                        style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}
                        onClick={(ev) => ev.stopPropagation()}
                      >
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <ActionButton
                            onClick={(e) => handleSendQuote(job, e)}
                            disabled={!!sendingQuoteReg}
                          >
                            {sendingQuoteReg === job.reg ? 'Sending...' : 'Email quote'}
                          </ActionButton>
                          <ActionButton
                            onClick={(e) => handleSendQuoteWhatsApp(job, e)}
                            disabled={!!sendingQuoteReg}
                          >
                            WhatsApp quote
                          </ActionButton>
                          <ActionButton variant="secondary" onClick={(e) => handleRecordQuoteGiven(job, 'phone', e)}>
                            Quoted by phone
                          </ActionButton>
                          <ActionButton variant="secondary" onClick={(e) => handleRecordQuoteGiven(job, 'in_person', e)}>
                            Quoted in person
                          </ActionButton>
                        </div>
                        <textarea
                          rows={2}
                          placeholder="Job approved over phone for £295"
                          value={jobApprovalNotesByReg[job.reg] ?? job.jobApprovalNote ?? ''}
                          onChange={(e) => {
                            e.stopPropagation();
                            setJobApprovalNotesByReg((prev) => ({ ...prev, [job.reg]: e.target.value }));
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            padding: '8px 10px',
                            borderRadius: '8px',
                            border: '1px solid var(--border, #e5e7eb)',
                            fontSize: '12px',
                            resize: 'vertical',
                            minHeight: '56px'
                          }}
                        />
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <ActionButton variant="secondary" onClick={(e) => handleApprove(job, e)}>
                            Authorise job
                          </ActionButton>
                          {isManager && (
                            <ActionButton variant="danger" onClick={(e) => handleReject(job, e)}>
                              Reject
                            </ActionButton>
                          )}
                        </div>
                      </div>
                    )}
                    </div>
                    </div>
                    )}
                  </div>
                );
        })}
            </div>
          </section>
        ))}
      </div>

      {jobsByTech.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: '48px 24px',
            color: 'var(--text-muted, #6b7280)'
          }}
        >
          {techSearch.trim()
            ? 'No technicians match that name.'
            : 'No jobs for this month.'}
        </div>
      )}
    </div>
  );
};

export default TechnicianDispatch;
